import { callTabCoordination } from '../services/callTabCoordination';
import { useCallStore } from '../store/callStore';
import { closeWindowIncomingCallNotification, type IncomingCallUiEffects } from './callListenerEffects';
import type { CallEndInfo } from './callTypes';

type CreateIncomingCoordinationParams = {
  incomingCallUiEffects: IncomingCallUiEffects;
  stopRingtone: () => void;
  postToServiceWorker: (message: any) => void;
  handleCallEnd: (info?: CallEndInfo) => void;
  incomingCallStaleTimeoutMs: number;
};

export function createIncomingCallCoordination(params: CreateIncomingCoordinationParams) {
  let incomingCallStaleTimer: number | null = null;

  const clearIncomingCallStaleTimer = () => {
    if (incomingCallStaleTimer !== null) {
      window.clearTimeout(incomingCallStaleTimer);
      incomingCallStaleTimer = null;
    }
  };

  const scheduleIncomingCallStaleTimeout = (callSessionId: string) => {
    clearIncomingCallStaleTimer();
    incomingCallStaleTimer = window.setTimeout(() => {
      const state = useCallStore.getState();
      const currentIncoming = state.incomingCall;
      if (!currentIncoming) return;
      if (currentIncoming.callSessionId !== callSessionId) return;

      params.handleCallEnd({ reason: 'incoming_stale_timeout', source: 'unknown' });
    }, params.incomingCallStaleTimeoutMs);
  };

  const unsubscribeCallStore = useCallStore.subscribe((state, prev) => {
    if (state.incomingCall?.callSessionId) {
      callTabCoordination.startManagingIncomingCall(state.incomingCall.callSessionId);
      params.incomingCallUiEffects.applyIncomingLeadershipState(state.incomingCall.callSessionId);
    }

    if (prev.incomingCall && !state.incomingCall) {
      params.stopRingtone();
      callTabCoordination.stopManagingIncomingCall(prev.incomingCall.callSessionId);

      try {
        const callSessionId = prev.incomingCall.callSessionId;
        closeWindowIncomingCallNotification(callSessionId);
        params.postToServiceWorker({ type: 'CLOSE_CALL_NOTIFICATION', callSessionId });
      } catch {
        // ignore
      }
    }
  });

  const unsubscribeCoordination = callTabCoordination.subscribe((event) => {
    if (event.type === 'state') {
      params.incomingCallUiEffects.applyIncomingLeadershipState(event.callSessionId);
      return;
    }

    params.incomingCallUiEffects.handleRemoteTabCallAction(event.callSessionId, event.action);
  });

  const cleanup = () => {
    clearIncomingCallStaleTimer();
    unsubscribeCoordination();
    unsubscribeCallStore();
  };

  return {
    clearIncomingCallStaleTimer,
    scheduleIncomingCallStaleTimeout,
    cleanup
  };
}
